import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, ShieldCheck, UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ROLES } from "@/pages/app/Team";
import { useRowKeys } from "@/lib/useRowKeys";

const Quiz = ({ quiz, set }) => {
  const [keys, drop] = useRowKeys(quiz.length);
  return (
  <div className="space-y-3">{quiz.map((q, i) => (
    <div key={keys[i]} className="rounded-xl border p-4">
      <div className="flex gap-2"><Input value={q.question} onChange={(e) => set(quiz.map((x, k) => (k === i ? { ...x, question: e.target.value } : x)))} placeholder="Question" data-testid={`quiz-q-${i}`} /><button onClick={() => { drop(i); set(quiz.filter((_, k) => k !== i)); }}><Trash2 className="h-4 w-4 text-alert" /></button></div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">{q.options.map((o, j) => (
        <label key={j} className="flex items-center gap-2"><input type="radio" checked={q.answer_index === j} onChange={() => set(quiz.map((x, k) => (k === i ? { ...x, answer_index: j } : x)))} title="Correct answer" />
          <Input value={o} onChange={(e) => set(quiz.map((x, k) => (k === i ? { ...x, options: x.options.map((y, l) => (l === j ? e.target.value : y)) } : x)))} /></label>
      ))}</div>
    </div>
  ))}<Button variant="outline" size="sm" onClick={() => set([...quiz, { question: "", options: ["", "", "", ""], answer_index: 0 }])} data-testid="quiz-add"><Plus className="mr-1 h-3 w-3" />Add question</Button></div>
  );
};

const Assign = ({ course, close }) => {
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState([]);
  const [due, setDue] = useState(new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10));
  useEffect(() => {
    api.get("/org/members").then((r) => setMembers(r.data));
  }, []);
  const go = async () => {
    try {
      const { data } = await api.post(`/training/courses/${course.id}/assign`, { user_ids: sel, due_date: due });
      toast.success(`Assigned to ${data.assigned} people — they've been notified by email`);
      close();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <ul className="max-h-64 space-y-1 overflow-auto">{members.map((m) => <li key={m.id}><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sel.includes(m.id)} onChange={(e) => setSel(e.target.checked ? [...sel, m.id] : sel.filter((x) => x !== m.id))} data-testid={`assign-train-${m.id}`} />{m.name} <span className="text-xs text-muted-foreground">{m.role}</span></label></li>)}</ul>
      <div className="space-y-1"><Label>Due date</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></div>
      <Button className="w-full" disabled={!sel.length} onClick={go} data-testid="assign-train-submit">Assign training</Button>
    </div>
  );
};

export default function CourseEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [pub, setPub] = useState(false);
  const [assign, setAssign] = useState(false);
  const [sig, setSig] = useState("");
  const [ok, setOk] = useState(false);
  const [secKeys, dropSec] = useRowKeys(c?.sections.length || 0);
  const load = () => api.get(`/training/courses/${id}`).then((r) => setC(r.data));
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!c) return <Loader />;
  const set = (k) => (v) => setC({ ...c, [k]: v?.target ? v.target.value : v });
  const save = async () => {
    try {
      const { data } = await api.put(`/training/courses/${id}`, c);
      setC(data);
      toast.success(data.status === "draft" && c.status === "published" ? "Saved — course returned to draft for re-review" : "Course saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const publish = async () => {
    try {
      await api.put(`/training/courses/${id}`, c);
      await api.post(`/training/courses/${id}/publish`, { confirm_competent_review: ok, reviewer_name: sig });
      toast.success("Course published");
      setPub(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="course-editor">
      <Link to="/app/training" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Training</Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><span className={`rounded-full px-2.5 py-0.5 text-xs ${c.status === "published" ? "bg-health/15 text-health" : "bg-[#FFA000]/15 text-[#B87300]"}`} data-testid="course-status">{c.status}</span>{c.reviewed_by && <span className="ml-2 text-xs text-muted-foreground">Reviewed by {c.reviewed_by}</span>}</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={save} data-testid="course-save">Save</Button>
          {c.status === "draft" ? <Button onClick={() => setPub(true)} data-testid="course-publish-open"><ShieldCheck className="mr-1.5 h-4 w-4" />Review & publish</Button> : <Button onClick={() => setAssign(true)} data-testid="course-assign-open"><UserPlus className="mr-1.5 h-4 w-4" />Assign</Button>}
          <Button variant="ghost" className="text-alert" onClick={() => window.confirm("Delete course?") && api.delete(`/training/courses/${id}`).then(() => nav("/app/training"))}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {c.ai_generated && c.status === "draft" && <div className="rounded-xl border border-[#FFA000]/30 bg-[#FFA000]/10 p-3 text-sm">AI-drafted course. A competent person must review all content and quiz answers before publication.</div>}
      <div className="grid gap-4 rounded-2xl border bg-card p-6 sm:grid-cols-4">
        <div className="space-y-1 sm:col-span-4"><Label>Title</Label><Input value={c.title} onChange={set("title")} data-testid="course-title" /></div>
        <div className="space-y-1 sm:col-span-4"><Label>Description</Label><Textarea rows={2} value={c.description || ""} onChange={set("description")} /></div>
        <div className="space-y-1"><Label>Duration (min)</Label><Input type="number" value={c.duration_minutes} onChange={(e) => set("duration_minutes")(Number(e.target.value))} /></div>
        <div className="space-y-1"><Label>Pass mark %</Label><Input type="number" value={c.pass_mark} onChange={(e) => set("pass_mark")(Number(e.target.value))} data-testid="course-pass-mark" /></div>
        <div className="space-y-1"><Label>Refresher (months)</Label><Input type="number" value={c.validity_months} onChange={(e) => set("validity_months")(Number(e.target.value))} /></div>
        <div className="space-y-1 sm:col-span-4"><Label>Required for roles (auto-assigned to new members)</Label>
          <div className="flex flex-wrap gap-1.5">{ROLES.map(([v, l]) => { const on = (c.required_roles || []).includes(v); return <button key={v} type="button" onClick={() => set("required_roles")(on ? c.required_roles.filter((x) => x !== v) : [...(c.required_roles || []), v])} className={`rounded-full border px-3 py-1 text-xs ${on ? "border-health bg-health/10" : ""}`} data-testid={`req-role-${v}`}>{l}</button>; })}</div></div>
        <div className="space-y-1 sm:col-span-4"><Label>Learning objectives (one per line)</Label><Textarea rows={3} value={(c.objectives || []).join("\n")} onChange={(e) => set("objectives")(e.target.value.split("\n"))} /></div>
      </div>
      <div className="space-y-3"><h2 className="text-lg font-semibold">Sections</h2>
        {c.sections.map((s, i) => <div key={secKeys[i]} className="space-y-2 rounded-2xl border bg-card p-5"><div className="flex gap-2"><Input value={s.heading} onChange={(e) => set("sections")(c.sections.map((x, k) => (k === i ? { ...x, heading: e.target.value } : x)))} className="font-semibold" /><button onClick={() => { dropSec(i); set("sections")(c.sections.filter((_, k) => k !== i)); }}><Trash2 className="h-4 w-4 text-alert" /></button></div><Textarea rows={6} value={s.content} onChange={(e) => set("sections")(c.sections.map((x, k) => (k === i ? { ...x, content: e.target.value } : x)))} /></div>)}
        <Button variant="outline" size="sm" onClick={() => set("sections")([...c.sections, { heading: "New section", content: "" }])}><Plus className="mr-1 h-3 w-3" />Add section</Button>
      </div>
      <div className="space-y-3"><h2 className="text-lg font-semibold">Quiz ({c.quiz.length} questions)</h2><Quiz quiz={c.quiz} set={set("quiz")} /></div>
      <Dialog open={pub} onOpenChange={setPub}><DialogContent><DialogHeader><DialogTitle>Competent review & publish</DialogTitle></DialogHeader>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1" data-testid="publish-confirm" />I am competent to review this content. I have checked the sections and quiz answers, and I understand that completing this course is an internal record, not an accredited qualification.</label>
        <Input placeholder={`Type your full name (${user.name})`} value={sig} onChange={(e) => setSig(e.target.value)} data-testid="publish-signature" />
        <Button onClick={publish} disabled={!ok} data-testid="publish-submit">Publish course</Button></DialogContent></Dialog>
      <Dialog open={assign} onOpenChange={setAssign}><DialogContent><DialogHeader><DialogTitle>Assign “{c.title}”</DialogTitle></DialogHeader>{assign && <Assign course={c} close={() => setAssign(false)} />}</DialogContent></Dialog>
    </div>
  );
}
